/**
 * Studio package discount guard — PKG-* codes only when the full package is in cart.
 * Clears stale codes when lines are removed or non-package items are added.
 * Keep slot counts in sync with package-config.js and package-rules.js.
 */
(function () {
  const PKG_PREFIX = 'PKG-GRAVITY-';
  let reconcileLock = false;

  function packages() {
    return window.GRAVITY_PACKAGES || {};
  }

  function shopifyRoot() {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) {
      return Shopify.routes.root;
    }
    return '/';
  }

  function cartJsonUrl(path) {
    const root = shopifyRoot();
    const base = root.endsWith('/') ? root.slice(0, -1) : root;
    return base + path;
  }

  function lineProp(item, key) {
    const props = item && item.properties;
    if (!props) return '';
    if (Array.isArray(props)) {
      for (let i = 0; i < props.length; i++) {
        if (props[i] && props[i].name === key) {
          return String(props[i].value || '');
        }
      }
      return '';
    }
    return props[key] != null ? String(props[key]) : '';
  }

  function expectedSlotCount(handle) {
    const config = packages()[handle];
    if (!config) return 0;
    let count = (config.slots || []).length;
    if (config.freeXs) count += 1;
    return count;
  }

  function packageLines(cart, handle) {
    return (cart.items || []).filter(function (item) {
      return lineProp(item, '_package_handle') === handle;
    });
  }

  /** Mageworx surcharge app lines — not studio packages. */
  function isSurchargeCartItem(item) {
    if (!item) return false;
    const productType = String(item.product_type || '').trim().toLowerCase();
    if (productType === 'mws_fee_generated') return true;
    const url = String(item.url || '').toLowerCase();
    if (
      url.indexOf('/products/handling-fee') !== -1 ||
      url.indexOf('/products/additional-fees') !== -1
    ) {
      return true;
    }
    const tags = String(item.tags || '').toLowerCase();
    if (tags.indexOf('mws_fee_generated') !== -1) return true;
    return false;
  }

  function isPackageCartItem(item) {
    if (!item || isSurchargeCartItem(item)) return false;
    if (lineProp(item, '_package_handle').trim()) return true;
    if (lineProp(item, '_package').trim()) return true;
    if (item.has_components) return true;
    const comps = item.item_components;
    return !!(comps && comps.length);
  }

  function hasMergedPackageBundle(cart) {
    return (cart.items || []).some(function (item) {
      if (isSurchargeCartItem(item)) return false;
      return item.has_components || (item.item_components && item.item_components.length);
    });
  }

  function nonPackageLineCount(cart) {
    return (cart.items || []).filter(function (item) {
      return !isPackageCartItem(item);
    }).length;
  }

  function hasAnyPackageLine(cart) {
    return (cart.items || []).some(isPackageCartItem);
  }

  function cartHasPackageContext(cart) {
    if (!cart) return false;
    if (hasAnyPackageLine(cart)) return true;
    if (hasMergedPackageBundle(cart)) return true;
    if (findQualifyingHandle(cart)) return true;
    const attrs = cart.attributes || {};
    return !!(attrs['Package handle'] || attrs['Studio package']);
  }

  function packageTargetCentsForCart(cart) {
    if (!cart) return 0;
    const attrs = cart.attributes || {};
    let handle = attrs['Package handle'] || findQualifyingHandle(cart) || '';
    if (!handle) {
      const handles = Object.keys(packages());
      for (let i = 0; i < handles.length; i++) {
        if (packagePricingMerged(cart, handles[i])) {
          handle = handles[i];
          break;
        }
      }
    }
    return handle ? packageTargetCents(handle) : 0;
  }

  /** Cart subtotal for display — package price only, never BOGO-adjusted. */
  function packageDisplayTotalCents(cart) {
    const target = packageTargetCentsForCart(cart);
    if (target > 0 && cartHasPackageContext(cart)) return target;
    return cart && cart.total_price ? cart.total_price : 0;
  }

  /** Studio packages use package pricing only — never stack with BOGO. */
  function promotionsExcludeBogo(cart) {
    return cartHasPackageContext(cart);
  }

  function cartHasMixedPackageAndIndividual(cart) {
    return hasAnyPackageLine(cart) && nonPackageLineCount(cart) > 0;
  }

  function otherPackageHandles(cart, handle) {
    const found = new Set();
    (cart.items || []).forEach(function (item) {
      const h = lineProp(item, '_package_handle').trim();
      if (h && h !== handle) found.add(h);
    });
    return found;
  }

  function hasUniqueSlots(lines) {
    const seen = new Set();
    for (let i = 0; i < lines.length; i++) {
      const slot = lineProp(lines[i], '_slot').trim();
      if (!slot) continue;
      if (seen.has(slot)) return false;
      seen.add(slot);
    }
    return true;
  }

  function packagePricingMerged(cart, handle) {
    const config = packages()[handle];
    if (!config || !cart || !cart.items) return false;

    const needed = expectedSlotCount(handle);
    const attrs = cart.attributes || {};
    const lines = packageLines(cart, handle);

    if (lines.length === 1) {
      const parent = lines[0];
      if (parent.has_components) return true;
      const comps = parent.item_components;
      if (comps && comps.length >= needed - 1) return true;
    }

    if (
      attrs['Package handle'] === handle &&
      (cart.items || []).some(function (item) {
        if (isSurchargeCartItem(item)) return false;
        return item.has_components || (item.item_components && item.item_components.length);
      })
    ) {
      return true;
    }

    const targetCents = config.packagePriceCents || 0;
    if (targetCents > 0 && lines.length >= needed) {
      const sum = lines.reduce(function (total, item) {
        return total + (item.final_line_price || item.line_price || 0);
      }, 0);
      if (sum > 0 && sum <= targetCents + 100) return true;
    }

    return false;
  }

  function cartQualifiesForHandle(cart, handle) {
    const needed = expectedSlotCount(handle);
    if (!needed || !cart || !cart.items || !cart.items.length) return false;
    if (nonPackageLineCount(cart) > 0) return false;
    if (otherPackageHandles(cart, handle).size > 0) return false;

    const lines = packageLines(cart, handle);
    if (lines.length !== needed) return false;
    return hasUniqueSlots(lines);
  }

  function findQualifyingHandle(cart) {
    const handles = Object.keys(packages());
    for (let i = 0; i < handles.length; i++) {
      if (cartQualifiesForHandle(cart, handles[i])) return handles[i];
    }
    return null;
  }

  function normalizePkgCode(value) {
    return String(value || '')
      .trim()
      .toUpperCase();
  }

  function isPkgCode(code) {
    return normalizePkgCode(code).indexOf(PKG_PREFIX) === 0;
  }

  function getAppliedPkgCodes(cart) {
    const codes = [];

    (cart.cart_level_discount_applications || []).forEach(function (app) {
      const title = normalizePkgCode(app.title || app.code || '');
      if (isPkgCode(title)) codes.push(title);
    });

    (cart.discount_codes || []).forEach(function (entry) {
      const code = normalizePkgCode(
        typeof entry === 'string' ? entry : entry.code || entry
      );
      if (isPkgCode(code)) codes.push(code);
    });

    const unique = [];
    codes.forEach(function (code) {
      if (unique.indexOf(code) === -1) unique.push(code);
    });
    return unique;
  }

  function packageTargetCents(handle) {
    const config = packages()[handle];
    return config && config.packagePriceCents ? config.packagePriceCents : 0;
  }

  function cartAtPackagePrice(cart, handle) {
    const targetCents = packageTargetCents(handle);
    if (!targetCents || !cart) return false;
    return cart.total_price > 0 && cart.total_price <= targetCents + 150;
  }

  function packagePricingApplied(cart, handle) {
    if (!handle || !cart) return false;
    if (packagePricingMerged(cart, handle)) return true;
    if (cartAtPackagePrice(cart, handle)) return true;

    const config = packages()[handle];
    const expectedCode = normalizePkgCode(config && config.discountCode);
    const applied = getAppliedPkgCodes(cart);
    if (expectedCode && applied.indexOf(expectedCode) !== -1) return true;

    if ((cart.total_discount || 0) > 0 && cartQualifiesForHandle(cart, handle)) {
      return true;
    }

    return false;
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  async function fetchCartState() {
    const res = await fetch(cartJsonUrl('/cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error('Failed to load cart (' + res.status + ')');
    return res.json();
  }

  async function setCartDiscount(code) {
    const res = await fetch(cartJsonUrl('/cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: code || '' }),
    });
    if (!res.ok) {
      const err = await res.json().catch(function () {
        return {};
      });
      throw new Error(
        err.description || err.message || 'Could not update cart discount'
      );
    }
    return res.json();
  }

  function applyDiscountViaIframe(code) {
    return new Promise(function (resolve) {
      const root = shopifyRoot();
      const prefix = root.endsWith('/') ? root : root + '/';
      const src =
        prefix +
        'discount/' +
        encodeURIComponent(code) +
        '?redirect=' +
        encodeURIComponent('/cart');
      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.setAttribute('tabindex', '-1');
      iframe.style.cssText =
        'position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none';
      let done = false;
      function finish() {
        if (done) return;
        done = true;
        try {
          iframe.remove();
        } catch (e) {
          /* ignore */
        }
        resolve();
      }
      iframe.onload = function () {
        setTimeout(finish, 600);
      };
      iframe.onerror = finish;
      document.body.appendChild(iframe);
      iframe.src = src;
      setTimeout(finish, 4000);
    });
  }

  async function applyPackageDiscountCode(code) {
    const normalized = normalizePkgCode(code);
    if (!normalized) return fetchCartState();

    await applyDiscountViaIframe(code);

    for (let attempt = 0; attempt < 8; attempt++) {
      await sleep(350);
      let cart = await fetchCartState();
      const applied = getAppliedPkgCodes(cart);
      if (applied.indexOf(normalized) !== -1) return cart;
      if ((cart.total_discount || 0) > 0) return cart;
    }

    try {
      const updated = await setCartDiscount(code);
      const applied = getAppliedPkgCodes(updated);
      if (applied.indexOf(normalized) !== -1) return updated;
      if ((updated.total_discount || 0) > 0) return updated;
    } catch (e) {
      /* update.js rejected — fall through */
    }

    await applyDiscountViaIframe(code);

    for (let attempt = 0; attempt < 8; attempt++) {
      await sleep(350);
      const cart = await fetchCartState();
      const applied = getAppliedPkgCodes(cart);
      if (applied.indexOf(normalized) !== -1) return cart;
      if ((cart.total_discount || 0) > 0) return cart;
    }

    return fetchCartState();
  }

  async function clearPackageDiscounts(cart) {
    const applied = getAppliedPkgCodes(cart);
    if (!applied.length) return cart;
    return setCartDiscount('');
  }

  /**
   * @param {object} cart
   * @returns {Promise<object>}
   */
  async function reconcile(cart) {
    if (!Object.keys(packages()).length) return cart;
    if (reconcileLock) return cart;

    const applied = getAppliedPkgCodes(cart);
    const empty = !cart.items || !cart.items.length;

    if (empty) {
      if (!applied.length) return cart;
      reconcileLock = true;
      try {
        return await clearPackageDiscounts(cart);
      } finally {
        reconcileLock = false;
      }
    }

    const handle = findQualifyingHandle(cart);
    const merged = handle ? packagePricingMerged(cart, handle) : false;

    if (!handle) {
      if (!applied.length) return cart;
      reconcileLock = true;
      try {
        return await clearPackageDiscounts(cart);
      } finally {
        reconcileLock = false;
      }
    }

    if (merged) {
      if (!applied.length) return cart;
      reconcileLock = true;
      try {
        return await clearPackageDiscounts(cart);
      } finally {
        reconcileLock = false;
      }
    }

    if (packagePricingApplied(cart, handle)) {
      return cart;
    }

    const config = packages()[handle];
    const expectedCode = normalizePkgCode(config && config.discountCode);
    if (!expectedCode) {
      if (!applied.length) return cart;
      reconcileLock = true;
      try {
        return await clearPackageDiscounts(cart);
      } finally {
        reconcileLock = false;
      }
    }

    reconcileLock = true;
    try {
      if (applied.length) {
        await clearPackageDiscounts(cart);
      }
      const updated = await applyPackageDiscountCode(config.discountCode);
      if (!packagePricingApplied(updated, handle)) {
        console.warn(
          '[GravityPackageDiscount] Package discount not applied. Expected code:',
          config.discountCode,
          'Cart total:',
          updated.total_price
        );
      }
      return updated;
    } finally {
      reconcileLock = false;
    }
  }

  window.GravityPackageDiscount = {
    PKG_PREFIX: PKG_PREFIX,
    expectedSlotCount: expectedSlotCount,
    cartQualifiesForHandle: cartQualifiesForHandle,
    hasAnyPackageLine: hasAnyPackageLine,
    cartHasPackageContext: cartHasPackageContext,
    promotionsExcludeBogo: promotionsExcludeBogo,
    cartHasMixedPackageAndIndividual: cartHasMixedPackageAndIndividual,
    packagePricingMerged: packagePricingMerged,
    packagePricingApplied: packagePricingApplied,
    packageDisplayTotalCents: packageDisplayTotalCents,
    packageTargetCentsForCart: packageTargetCentsForCart,
    isPackageCartItem: isPackageCartItem,
    isSurchargeCartItem: isSurchargeCartItem,
    findQualifyingHandle: findQualifyingHandle,
    getAppliedPkgCodes: getAppliedPkgCodes,
    reconcile: reconcile,
    fetchCartState: fetchCartState,
  };
})();
